import Component from '@bythepixel/component-loader';

/* global YT */

/**
 * YouTube player configuration
 */
const YOUTUBE_CONFIG = {
  autoplay: 1,
  rel: 0, // Don't show related videos from other channels
  modestbranding: 1, // Minimal YouTube branding
};

/**
 * CSS class names used for showing/hiding elements
 */
const CSS_CLASSES = {
  hidden: 'hidden',
  playing: 'promotional-video--playing',
};

/**
 * Keyboard key codes
 */
const KEYS = {
  escape: 'Escape',
};

export default class PromotionalVideo extends Component {
  static selector = '[data-comp-promotional-video]';

  #videoId = null;
  #player = null;
  #contentLayer = null;
  #videoLayer = null;
  #activatorButton = null;
  #closeButton = null;
  #boundKeyHandler = null;
  #isYouTubeAPIReady = false;

  constructor(...args) {
    super(...args);
    this.init();
  }

  init() {
    // Get video ID from data attribute
    this.#videoId = this.$container.dataset.videoId;

    if (!this.#videoId) {
      console.warn('PromotionalVideo: No video ID provided');
      return;
    }

    // Cache DOM elements
    this.#contentLayer = this.$container.querySelector('[data-content-layer]');
    this.#videoLayer = this.$container.querySelector('[data-video-layer]');
    this.#activatorButton = this.$container.querySelector('[data-video-activator]');
    this.#closeButton = this.$container.querySelector('[data-video-close]');

    if (!this.#activatorButton) {
      console.warn('PromotionalVideo: No activator button found with [data-video-activator]');
      return;
    }

    // Load YouTube API
    this.loadYouTubeAPI();

    // Set up event listeners
    this.events();
  }

  /**
   * Load the YouTube IFrame API
   */
  loadYouTubeAPI() {
    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      this.#isYouTubeAPIReady = true;
      return;
    }

    // Check if API script is already being loaded
    if (window.onYouTubeIframeAPIReady) {
      // API is loading, add our callback to the queue
      const originalCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        originalCallback();
        this.onYouTubeAPIReady();
      };
      return;
    }

    // Load the API script
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Set up the callback
    window.onYouTubeIframeAPIReady = () => this.onYouTubeAPIReady();
  }

  /**
   * Called when YouTube API is ready
   */
  onYouTubeAPIReady() {
    this.#isYouTubeAPIReady = true;
  }

  /**
   * Set up event listeners
   */
  events() {
    // Activator button click
    if (this.#activatorButton) {
      this.#activatorButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.showVideo();
      });
    }

    // Close button click
    if (this.#closeButton) {
      this.#closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideVideo();
      });
    }

    // ESC key handler
    this.#boundKeyHandler = (e) => this.handleKeyPress(e);
    document.addEventListener('keydown', this.#boundKeyHandler);
  }

  /**
   * Handle keyboard events
   */
  handleKeyPress(e) {
    // Only handle ESC when video is showing
    if (e.key === KEYS.escape && !this.#videoLayer.classList.contains(CSS_CLASSES.hidden)) {
      this.hideVideo();
    }
  }

  /**
   * Show the video and hide the content
   */
  showVideo() {
    if (!this.#isYouTubeAPIReady) {
      console.warn('YouTube API not ready yet');
      return;
    }

    // Add playing class to container for mobile height fix
    this.$container.classList.add(CSS_CLASSES.playing);

    // Hide content layer
    if (this.#contentLayer) {
      this.#contentLayer.classList.add(CSS_CLASSES.hidden);
    }

    // Show video layer
    if (this.#videoLayer) {
      this.#videoLayer.classList.remove(CSS_CLASSES.hidden);
    }

    // Create YouTube player if it doesn't exist
    if (!this.#player) {
      this.createPlayer();
    } else {
      // If player exists, play the video
      this.#player.playVideo();
    }
  }

  /**
   * Hide the video and show the content
   */
  hideVideo() {
    // Remove playing class from container
    this.$container.classList.remove(CSS_CLASSES.playing);

    // Show content layer
    if (this.#contentLayer) {
      this.#contentLayer.classList.remove(CSS_CLASSES.hidden);
    }

    // Hide video layer
    if (this.#videoLayer) {
      this.#videoLayer.classList.add(CSS_CLASSES.hidden);
    }

    // Stop the video
    if (this.#player) {
      this.#player.stopVideo();
    }
  }

  /**
   * Create the YouTube player
   */
  createPlayer() {
    const playerId = `youtube-player-${this.#videoId}`;

    this.#player = new YT.Player(playerId, {
      videoId: this.#videoId,
      playerVars: YOUTUBE_CONFIG,
      events: {
        onReady: (event) => {
          // Player is ready and will autoplay
          event.target.playVideo();
        },
      },
    });
  }

  /**
   * Cleanup when component is destroyed
   */
  destroy() {
    // Destroy YouTube player
    if (this.#player) {
      this.#player.destroy();
      this.#player = null;
    }

    // Remove event listeners
    if (this.#boundKeyHandler) {
      document.removeEventListener('keydown', this.#boundKeyHandler);
      this.#boundKeyHandler = null;
    }

    // Remove click handlers (they'll be garbage collected with the DOM elements)
  }
}
